/**
 * Video Grid Section
 * Handles video playback, fullscreen modal, autoplay functionality, and swiper carousel
 */

class VideoGridSection extends HTMLElement {
  constructor() {
    super();
    this.swiper = null;
    this.modal = null;
    this.modalContent = null;
    this.activeVideo = null;
    this.autoplayObserver = null;
    this.resizeTimeout = null;
  }

  connectedCallback() {
    // Wait for Swiper to be available if carousel is enabled
    if (this.querySelector('.video-grid-swiper')) {
      this.waitForSwiper().then(() => this.init());
    } else {
      this.init();
    }
  }

  waitForSwiper(maxAttempts = 50) {
    return new Promise((resolve) => {
      let attempts = 0;
      const check = () => {
        attempts++;
        if (typeof Swiper !== 'undefined') {
          resolve();
        } else if (attempts < maxAttempts) {
          setTimeout(check, 100);
        } else {
          // Swiper not available, initialize without it
          console.warn('VideoGrid: Swiper not loaded, falling back to grid mode');
          resolve();
        }
      };
      check();
    });
  }

  disconnectedCallback() {
    this.destroySwiper();
    window.removeEventListener('resize', this.handleResize);
  }

  init() {
    this.wrapper = this.querySelector('.video-grid-wrapper');
    this.swiperContainer = this.querySelector('.video-grid-swiper__container');
    
    this.bindEvents();
    this.setupAutoplay();
    this.initSwiper();

    // Handle resize for responsive swiper updates
    this.handleResize = this.debounce(() => {
      this.updateSwiperOnResize();
    }, 250);
    window.addEventListener('resize', this.handleResize);
  }

  getSettings() {
    if (!this.wrapper) return {};
    
    return {
      carouselMobile: this.wrapper.dataset.carouselMobile === 'true',
      carouselDesktop: this.wrapper.dataset.carouselDesktop === 'true',
      blockCount: parseInt(this.wrapper.dataset.blockCount, 10) || 0,
      columnsDesktop: parseInt(this.wrapper.dataset.columnsDesktop, 10) || 3,
      columnsTablet: parseInt(this.wrapper.dataset.columnsTablet, 10) || 2,
      columnsMobile: parseInt(this.wrapper.dataset.columnsMobile, 10) || 1,
      loop: this.wrapper.dataset.loop === 'true',
      autoplay: this.wrapper.dataset.autoplay === 'true',
      autoplaySpeed: parseInt(this.wrapper.dataset.autoplaySpeed, 10) || 5000,
      showNavigation: this.wrapper.dataset.showNavigation === 'true',
      showPagination: this.wrapper.dataset.showPagination === 'true',
      slidesPerViewMobile: parseFloat(this.wrapper.dataset.slidesPerViewMobile) || 1.2,
    };
  }

  isMobile() {
    return window.innerWidth < 990;
  }

  isTablet() {
    return window.innerWidth >= 750 && window.innerWidth < 990;
  }

  shouldUseCarousel() {
    const settings = this.getSettings();
    const isMobile = this.isMobile();
    
    if (isMobile && settings.carouselMobile) {
      return true;
    }
    
    if (!isMobile && settings.carouselDesktop) {
      return true;
    }
    
    return false;
  }

  isSafari() {
    const ua = navigator.userAgent.toLowerCase();
    return ua.includes('safari') && !ua.includes('chrome') && !ua.includes('android');
  }

  initSwiper() {
    if (!this.swiperContainer || typeof Swiper === 'undefined') return;
    
    const settings = this.getSettings();
    const swiperWrapper = this.querySelector('.video-grid-swiper');
    
    if (!swiperWrapper) return;

    // Get gap values from CSS custom properties
    const gridElement = this.querySelector('.video-grid');
    const computedStyle = gridElement ? getComputedStyle(gridElement) : null;
    const gapMobile = computedStyle ? parseFloat(computedStyle.getPropertyValue('--grid-gap')) || 1.6 : 1.6;
    const gapDesktop = computedStyle ? parseFloat(computedStyle.getPropertyValue('--grid-gap-lg')) || 2 : 2;
    const gapMobilePx = gapMobile * 10; // Convert rem to approximate px
    const gapDesktopPx = gapDesktop * 10;

    // Use cssMode on Safari/iOS for better touch scrolling reliability
    const useCssMode = this.isSafari();

    const swiperOptions = {
      slidesPerView: settings.slidesPerViewMobile,
      spaceBetween: gapMobilePx,
      grabCursor: true,
      watchOverflow: true,
      loop: settings.loop && settings.blockCount > 1 && !useCssMode, // Loop not supported with cssMode
      cssMode: useCssMode,
      breakpoints: {
        750: {
          slidesPerView: settings.columnsTablet,
          spaceBetween: gapDesktopPx,
        },
        990: {
          slidesPerView: settings.columnsDesktop,
          spaceBetween: gapDesktopPx,
        }
      }
    };

    // Add navigation if enabled
    if (settings.showNavigation) {
      swiperOptions.navigation = {
        nextEl: swiperWrapper.querySelector('.video-grid-nav__arrow--next'),
        prevEl: swiperWrapper.querySelector('.video-grid-nav__arrow--prev'),
      };
    }

    // Add pagination if enabled
    if (settings.showPagination) {
      swiperOptions.pagination = {
        el: swiperWrapper.querySelector('.video-grid-nav__dots'),
        clickable: true,
      };
    }

    // Add autoplay if enabled
    if (settings.autoplay) {
      swiperOptions.autoplay = {
        delay: settings.autoplaySpeed,
        disableOnInteraction: true,
        pauseOnMouseEnter: true,
      };
    }

    // Initialize Swiper
    this.swiper = new Swiper(this.swiperContainer, swiperOptions);
  }

  destroySwiper() {
    if (this.swiper) {
      this.swiper.destroy(true, true);
      this.swiper = null;
    }
  }

  updateSwiperOnResize() {
    if (this.swiper) {
      this.swiper.update();
    }
  }

  bindEvents() {
    // Delegate click events for video posters
    this.addEventListener('click', (e) => {
      const poster = e.target.closest('.video-grid__poster');
      if (poster) {
        this.handlePosterClick(poster);
      }
      
      const closeBtn = e.target.closest('.video-grid-modal__close');
      if (closeBtn) {
        this.closeModal();
      }
    });
    
    // Close modal on backdrop click
    this.addEventListener('click', (e) => {
      if (e.target.classList.contains('video-grid-modal')) {
        this.closeModal();
      }
    });
    
    // Close modal on Escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.modal?.classList.contains('is-active')) {
        this.closeModal();
      }
    });
    
    // Handle Shopify editor events
    if (Shopify.designMode) {
      document.addEventListener('shopify:section:load', (e) => {
        if (this.contains(e.target) || e.target.contains(this)) {
          this.destroySwiper();
          this.init();
        }
      });
      
      document.addEventListener('shopify:block:select', (e) => {
        if (this.swiper && this.contains(e.target)) {
          const slideIndex = [...this.querySelectorAll('.swiper-slide')].findIndex(
            slide => slide.contains(e.target)
          );
          if (slideIndex !== -1) {
            this.swiper.slideTo(slideIndex);
          }
        }
      });
    }
  }
  
  handlePosterClick(poster) {
    const mediaContainer = poster.closest('.video-grid__media');
    if (!mediaContainer) return;
    
    const videoType = mediaContainer.dataset.videoType;
    if (videoType === 'none') return;
    
    const fullscreen = mediaContainer.dataset.fullscreen === 'true';
    
    if (fullscreen) {
      this.openInModal(mediaContainer);
    } else {
      this.playInline(mediaContainer);
    }
  }
  
  playInline(mediaContainer) {
    const template = mediaContainer.querySelector('template');
    if (!template) return;
    
    const content = template.content.cloneNode(true);
    const wrap = document.createElement('div');
    wrap.className = 'video-grid__video-wrap';
    wrap.appendChild(content);
    
    mediaContainer.appendChild(wrap);
    mediaContainer.dataset.loaded = 'true';
    
    // Handle video element
    const video = wrap.querySelector('video');
    if (video) {
      video.play().catch(() => {});
    }
  }
  
  openInModal(mediaContainer) {
    // Find the modal in the same section
    this.modal = this.querySelector('.video-grid-modal');
    
    if (!this.modal) return;
    
    this.modalContent = this.modal.querySelector('.video-grid-modal__content');
    if (!this.modalContent) return;
    
    // Clone template content
    const template = mediaContainer.querySelector('template');
    if (!template) return;
    
    const content = template.content.cloneNode(true);
    this.modalContent.innerHTML = '';
    this.modalContent.appendChild(content);
    
    // Show modal
    this.modal.classList.add('is-active');
    this.modal.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    
    // Focus close button
    const closeBtn = this.modal.querySelector('.video-grid-modal__close');
    closeBtn?.focus();
    
    // Handle video element
    const video = this.modalContent.querySelector('video');
    if (video) {
      video.play().catch(() => {});
      this.activeVideo = video;
    }
  }
  
  closeModal() {
    if (!this.modal) return;
    
    // Stop and clean up video
    if (this.activeVideo) {
      this.activeVideo.pause();
      this.activeVideo = null;
    }
    
    // Stop iframes
    const iframe = this.modalContent?.querySelector('iframe');
    if (iframe) {
      iframe.src = '';
    }
    
    // Clear content
    if (this.modalContent) {
      this.modalContent.innerHTML = '';
    }
    
    // Hide modal
    this.modal.classList.remove('is-active');
    this.modal.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    
    this.modal = null;
    this.modalContent = null;
  }
  
  setupAutoplay(root = this) {
    const autoplayItems = root.querySelectorAll('.video-grid__media[data-autoplay="true"]');
    if (!autoplayItems.length) return;
    
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.handleAutoplay(entry.target);
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.5 }
    );
    
    autoplayItems.forEach((item) => observer.observe(item));
  }
  
  handleAutoplay(mediaContainer) {
    // Only autoplay if fullscreen is disabled
    if (mediaContainer.dataset.fullscreen === 'true') return;
    if (mediaContainer.dataset.loaded === 'true') return;
    
    const template = mediaContainer.querySelector('template');
    if (!template) return;
    
    const content = template.content.cloneNode(true);
    
    // For autoplay, we need to mute the video
    const video = content.querySelector('video');
    if (video) {
      video.muted = true;
    }
    
    // For iframes, add mute parameter
    const iframe = content.querySelector('iframe');
    if (iframe) {
      const src = iframe.src;
      if (src.includes('youtube.com')) {
        iframe.src = src + '&mute=1';
      } else if (src.includes('vimeo.com')) {
        iframe.src = src + '&muted=1';
      }
    }
    
    const wrap = document.createElement('div');
    wrap.className = 'video-grid__video-wrap';
    wrap.appendChild(content);
    
    mediaContainer.appendChild(wrap);
    mediaContainer.dataset.loaded = 'true';
    
    if (video) {
      video.play().catch(() => {});
    }
  }

  debounce(func, wait) {
    let timeout;
    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => func.apply(this, args), wait);
    };
  }
}

// Define custom element
customElements.define('video-grid-section', VideoGridSection);
